import { useMemo } from 'react';
import { uniq } from 'lodash';

import { ConsolidatedForecastReportTab, VIEW_TYPES } from '../../../../core/types';
import { REPORT_SECTION } from '../../../../core/constants';
import { usePredictionsInfoContext } from '../../context/predictions-info-context';
import { useSubTabNameMaps } from './useSubTabNameMaps';

const viewTypeReportSectionMap: Record<string, REPORT_SECTION> = {
  [VIEW_TYPES.ACCRUALS_BS]: REPORT_SECTION.BalanceSheet,
  [VIEW_TYPES.ACCRUALS_PL]: REPORT_SECTION.ProfitLoss,
  [VIEW_TYPES.CASH_FLOW]: REPORT_SECTION.CashFlow
};

const reportSectionViewTypeMap: Record<string, VIEW_TYPES> = {
  [REPORT_SECTION.BalanceSheet]: VIEW_TYPES.ACCRUALS_BS,
  [REPORT_SECTION.ProfitLoss]: VIEW_TYPES.ACCRUALS_PL,
  [REPORT_SECTION.CashFlow]: VIEW_TYPES.CASH_FLOW
};

/**
 * Hook to get view options and the selected view option (and corresponding report section)
 * for a given forecast tab
 */
export const useForecastTabViewOptions = (tab: ConsolidatedForecastReportTab) => {
  const { viewNames } = useSubTabNameMaps();
  const { viewType } = usePredictionsInfoContext();

  const { viewOptions, viewTypeIds } = useMemo(() => {
    const reportSectionIdsFromTabs = tab.children.map(child => child.find_in) as REPORT_SECTION[];
    const reportSectionIds = uniq([...reportSectionIdsFromTabs, REPORT_SECTION.CashFlow]);

    const viewOptions = reportSectionIds.map(reportSectionId => ({
      name: viewNames[reportSectionViewTypeMap[reportSectionId]],
      value: reportSectionViewTypeMap[reportSectionId]
    }));

    const viewTypeIds = reportSectionIds.map(id => reportSectionViewTypeMap[id]);

    return { viewOptions, reportSectionIds, viewTypeIds };
  }, [viewNames, tab]);

  const selectedViewType = viewType && viewTypeIds.includes(viewType as VIEW_TYPES) ? viewType : viewTypeIds[0];
  const selectedReportSection = viewTypeReportSectionMap[selectedViewType];

  return {
    isCashFlow: selectedReportSection === REPORT_SECTION.CashFlow,
    viewOptions,
    selectedViewType,
    selectedReportSection
  };
};
